import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LABEL_INDENT_PX } from './treeGeometry';

import { MaterialSymbol } from '@/components/MaterialSymbol';

/*
  Keyed under `pm.planning.` so the e2e helpers' prefix sweep clears it between
  spec files, as they do for the List/Timeline choice.
*/
const HINT_SEEN_KEY = 'pm.planning.hintSeen';

const readSeen = (): boolean => {
  try {
    return window.localStorage.getItem(HINT_SEEN_KEY) === 'true';
  } catch {
    // Private mode and "block site data" both throw here. Showing the hints
    // again is a better failure than crashing a row edit.
    return false;
  }
};

const markSeen = (): void => {
  try {
    window.localStorage.setItem(HINT_SEEN_KEY, 'true');
  } catch {
    /* see readSeen */
  }
};

/**
 * Keeps the bar alive through a click on its own controls.
 *
 * This bar renders only while a row field is being edited. A plain button takes
 * focus on mousedown, which blurs the input, which commits the edit and
 * unmounts the bar — so the click landed on something that no longer existed
 * and appeared to do nothing at all.
 */
const keepFocus = (event: { preventDefault: () => void }) => {
  event.preventDefault();
};

const Key = ({ children }: { children: string }) => (
  <Box
    component="kbd"
    sx={{
      fontFamily: 'var(--font-family-mono)',
      fontSize: 10,
      bgcolor: 'var(--bg-panel)',
      border: 'var(--border-width-thin) solid var(--border-divider)',
      borderRadius: '4px',
      px: 1,
      py: 0.5,
      color: 'var(--fg-1)',
    }}
  >
    {children}
  </Box>
);

const Hint = ({ keys, label }: { keys: string; label: string }) => (
  <Box
    component="span"
    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 11, color: 'var(--fg-2)' }}
  >
    <Key>{keys}</Key>
    {label}
  </Box>
);

/**
 * The keyboard-shortcut hint bar: open on the FIRST inline edit, a "Shortcuts"
 * chip on every edit after that, expanding on click.
 *
 * It used to stay open for the first three edits, counted in localStorage from
 * inside a `useMemo` — a write during render, which React's StrictMode
 * double-invokes. The allowance burned out in under two tasks in dev, which is
 * why it seemed to vanish almost immediately. The state is now a plain "have
 * you seen it", written from an effect where a side effect belongs.
 */
export const KeyboardHintBar = () => {
  const { t } = useTranslation('pmPlanning');
  // Read once, before the effect below records this sighting — otherwise the
  // first edit would already count as seen and never expand.
  const [seen] = useState(readSeen);
  const [expanded, setExpanded] = useState(!seen);

  useEffect(() => {
    markSeen();
  }, []);

  if (!expanded) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          pl: `${String(LABEL_INDENT_PX)}px`,
          py: 0.5,
        }}
      >
        <Box
          component="button"
          type="button"
          data-automation-id="shortcuts-chip"
          onMouseDown={keepFocus}
          onClick={(event) => {
            event.stopPropagation();
            setExpanded(true);
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            pl: 1,
            pr: 1,
            py: 0.5,
            border: 'none',
            borderRadius: 'var(--border-radius-pill)',
            bgcolor: 'var(--color-blue-10)',
            fontSize: 11,
            fontWeight: 500,
            fontFamily: 'var(--font-family)',
            color: 'var(--color-blue-100)',
            cursor: 'pointer',
            '&:hover': { bgcolor: 'var(--color-blue-20)' },
          }}
        >
          <MaterialSymbol name="keyboard" size={15} />
          {t('keyboardHints.Shortcuts')}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      data-automation-id="keyboard-hint-bar"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
        px: 2,
        pl: `${String(LABEL_INDENT_PX)}px`,
        py: 1,
        bgcolor: 'var(--color-blue-10)',
        borderTop: 'var(--border-width-thin) solid var(--border-divider)',
        animation: 'su var(--duration-fast) ease-out',
      }}
    >
      <Box
        component="span"
        sx={{
          fontSize: 11,
          fontWeight: 500,
          color: 'var(--color-blue-100)',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <MaterialSymbol name="keyboard" size={14} />
        {t('keyboardHints.Keyboard')}
      </Box>
      <Hint keys="Tab" label={t('keyboardHints.NextField')} />
      <Hint keys="↵" label={t('keyboardHints.Finish')} />
      <Hint keys="⌘ ↵" label={t('keyboardHints.NewTask')} />
      <Hint keys="⌘ ⇧ ↵" label={t('keyboardHints.SubTask')} />
      <Hint keys="⌘ ⌥ ↵" label={t('keyboardHints.LevelUp')} />
      <Hint keys="⌥ ←/→" label={t('keyboardHints.ChangeLevel')} />
      {seen ? (
        <Box
          component="button"
          type="button"
          title={t('keyboardHints.HideShortcuts')}
          data-automation-id="shortcuts-hide"
          onMouseDown={keepFocus}
          onClick={(event) => {
            event.stopPropagation();
            setExpanded(false);
          }}
          sx={{
            ml: 'auto',
            width: 22,
            height: 22,
            flex: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            borderRadius: 'var(--border-radius-pill)',
            bgcolor: 'transparent',
            color: 'var(--color-blue-100)',
            cursor: 'pointer',
            '&:hover': { bgcolor: 'var(--bg-panel)' },
          }}
        >
          <MaterialSymbol name="close" size={16} />
        </Box>
      ) : null}
    </Box>
  );
};
